import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import VerificationStep1 from "./steps/VerificationStep1";
import VerificationStep2 from "./steps/VerificationStep2";
import VerificationStep3 from "./steps/VerificationStep3";
import VerificationStep4 from "./steps/VerificationStep4";
import { useNavigate, useLocation } from "react-router-dom";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { isFileReference } from "@/utils/temporaryFileStorage";

interface VerificationStepperProps {
  onComplete: () => void;
}

// Define a type for form data to store in session storage
interface VerificationFormData {
  personalInfo: {
    first_name: string;
    middle_name: string;
    last_name: string;
    residency_status: string;
    date_of_birth?: string | Date;
  };
  verificationCountry: string;
  documents: Array<{
    country_code: string;
    document_type_id: string;
    document_number: string;
    issue_date?: Date;
    expiry_date?: Date;
    document_file?: any;
    verification_purpose: "primary" | "expatriate" | "both";
    verification_country?: string;
  }>;
}

const VerificationStepper = ({ onComplete }: VerificationStepperProps) => {
  const { profile, user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isStep1Valid, setIsStep1Valid] = useState(false);
  const [formData, setFormData] = useState<VerificationFormData>({
    personalInfo: {
      first_name: profile?.first_name || "",
      middle_name: profile?.middle_name || "",
      last_name: profile?.last_name || "",
      residency_status: profile?.residency_status || "",
      date_of_birth: profile?.date_of_birth || undefined,
    },
    verificationCountry: profile?.country_code || "",
    documents: [],
  });

  // Prepare form data for session storage - remove File objects
  const prepareFormDataForStorage = (data: any): any => {
    // Create a clean version for storage
    const cleanData = { ...data };
    
    // Handle documents array if it exists
    if (cleanData.documents && Array.isArray(cleanData.documents)) {
      cleanData.documents = cleanData.documents.map((doc: any) => {
        const cleanDoc = { ...doc };
        
        // Convert dates to ISO strings for consistent storage
        if (cleanDoc.issue_date instanceof Date) {
          cleanDoc.issue_date = cleanDoc.issue_date.toISOString();
        }
        
        if (cleanDoc.expiry_date instanceof Date) {
          cleanDoc.expiry_date = cleanDoc.expiry_date.toISOString();
        }
        
        // The document_file is already a FileReference if it was processed correctly
        // We keep it as is since it's just a simple object with strings
        
        return cleanDoc;
      });
    }
    
    return cleanData;
  };

  // Restore form data from session storage - handle saved dates
  const restoreFormDataFromStorage = (storedData: any): any => {
    if (!storedData) return null;
    
    const restoredData = { ...storedData };
    
    // Restore date objects
    if (restoredData.personalInfo?.date_of_birth) {
      restoredData.personalInfo.date_of_birth = new Date(restoredData.personalInfo.date_of_birth);
    }
    
    // Restore document dates
    if (restoredData.documents && Array.isArray(restoredData.documents)) {
      restoredData.documents = restoredData.documents.map((doc: any) => {
        const restoredDoc = { ...doc };
        
        if (restoredDoc.issue_date) {
          restoredDoc.issue_date = new Date(restoredDoc.issue_date);
        }
        
        if (restoredDoc.expiry_date) {
          restoredDoc.expiry_date = new Date(restoredDoc.expiry_date);
        }
        
        return restoredDoc;
      });
    }
    
    return restoredData;
  };

  // Load saved state from session storage
  useEffect(() => {
    // Ensure authentication is complete and user is authenticated
    if (!isLoading) {
      if (!isAuthenticated) {
        navigate('/auth', { state: { from: '/verification-process' + location.search } });
        return;
      }
      
      try {
        // Try to load saved data from session storage
        const savedFormData = sessionStorage.getItem('verificationFormData');
        const savedStep = sessionStorage.getItem('verificationCurrentStep');
        
        if (savedFormData) {
          const parsedData = JSON.parse(savedFormData);
          const restoredData = restoreFormDataFromStorage(parsedData);
          setFormData(restoredData);
        }
        
        if (savedStep) {
          setCurrentStep(parseInt(savedStep, 10));
        }
        
        // Get step from URL query param if present
        const params = new URLSearchParams(location.search);
        const stepParam = params.get('step');
        if (stepParam) {
          const step = parseInt(stepParam, 10) - 1; // Convert to 0-based index
          if (!isNaN(step) && step >= 0 && step < 4) {
            setCurrentStep(step);
          }
        }
      } catch (error) {
        console.error('Error loading verification data from session storage:', error);
        // If there's an error loading data, just continue with default values
      }
    }
  }, [isLoading, isAuthenticated, navigate, location.search, profile]);

  // Update form data when profile changes
  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        personalInfo: {
          first_name: profile.first_name || prev.personalInfo.first_name,
          middle_name: profile.middle_name || prev.personalInfo.middle_name,
          last_name: profile.last_name || prev.personalInfo.last_name,
          residency_status: profile.residency_status || prev.personalInfo.residency_status,
          date_of_birth: profile.date_of_birth || prev.personalInfo.date_of_birth,
        },
        verificationCountry: profile.country_code || prev.verificationCountry,
      }));
    }
  }, [profile]);

  // Save current state to session storage whenever it changes
  useEffect(() => {
    if (isAuthenticated) {
      try {
        // Prepare data for storage by removing file objects
        const cleanFormData = prepareFormDataForStorage(formData);
        
        sessionStorage.setItem('verificationFormData', JSON.stringify(cleanFormData));
        sessionStorage.setItem('verificationCurrentStep', currentStep.toString());
      } catch (error) {
        console.error('Error saving verification data to session storage:', error);
      }
    }
  }, [formData, currentStep, isAuthenticated]);

  const steps = [
    {
      title: "Introduction",
      component: (
        <VerificationStep1
          formData={formData}
          setFormData={setFormData}
          setStepValid={setIsStep1Valid}
        />
      ),
    },
    {
      title: "Personal Information",
      component: (
        <VerificationStep2 formData={formData} setFormData={setFormData} />
      ),
    },
    {
      title: "Document Upload",
      component: (
        <VerificationStep3 formData={formData} setFormData={setFormData} />
      ),
    },
    {
      title: "Review & Submit",
      component: (
        <VerificationStep4 formData={formData} onSubmit={onComplete} />
      ),
    },
  ];

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return isStep1Valid;
      case 1:
        return !!(
          formData.personalInfo.first_name &&
          formData.personalInfo.last_name &&
          formData.personalInfo.date_of_birth
        );
      case 2:
        return (
          Array.isArray(formData.documents) && formData.documents.length > 0
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!isStepValid()) return;
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // Update URL with the step number (1-based for users)
      navigate(`/verification-process?step=${nextStep + 1}`, { replace: true });
      window.scrollTo(0, 0);
    } else {
      // Clear session storage when completing the process
      sessionStorage.removeItem('verificationFormData');
      sessionStorage.removeItem('verificationCurrentStep');
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      // Update URL with the step number (1-based for users)
      navigate(`/verification-process?step=${prevStep + 1}`, { replace: true });
      window.scrollTo(0, 0);
    }
  };

  const isLastStep = currentStep === steps.length - 1;

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-purple"></div>
      </div>
    );
  }

  // Redirect if no user
  if (!isAuthenticated) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertTriangle className="h-5 w-5" />
        <AlertTitle>Authentication Required</AlertTitle>
        <AlertDescription className="mt-2">
          <p className="mb-4">You must be logged in to access the verification process.</p>
          <Button 
            onClick={() => navigate('/auth', { state: { from: '/verification-process' + location.search } })}
            variant="outline"
          >
            Sign In
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-4">Identity Verification</h1>
        <p className="text-muted-foreground">
          Step {currentStep + 1} of {steps.length}: {steps[currentStep].title}
        </p>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
        <div
          className="bg-nation-purple h-2.5 rounded-full transition-all duration-300"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>

      <Card className="p-6">{steps[currentStep].component}</Card>

      <div className="flex justify-between pt-4">
        {currentStep > 0 ? (
          <Button
            type="button"
            variant="outline"
            onClick={handlePrevious}
            className="flex items-center"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Previous
          </Button>
        ) : (
          <div />
        )}

        {!isLastStep && (
          <Button
            type="button"
            onClick={handleNext}
            className="bg-nation-purple hover:bg-nation-purple-dark"
            disabled={!isStepValid()}
          >
            Next <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default VerificationStepper;
